'use client';

import React from 'react';
import { LockClosedIcon, UserCircleIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import Link from 'next/link';

interface SignInPromptModalProps {
  onClose: () => void;
  action?: string; // e.g. "report a scam", "post", "connect"
}

export default function SignInPromptModal({ onClose, action = 'perform this action' }: SignInPromptModalProps) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-2xl w-full max-w-sm shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-col items-center gap-3 px-6 pt-7 pb-4">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
            <LockClosedIcon className="text-primary" style={{ width: 28, height: 28 }} />
          </div>
          <h2 className="text-lg font-bold text-foreground text-center">Sign In Required</h2>
          <p className="text-sm text-muted-foreground text-center leading-relaxed">
            You need to be signed in to {action}. Create a free account or sign in to get started.
          </p>
        </div>

        {/* Features */}
        <div className="mx-6 mb-5 p-3 bg-muted/50 rounded-xl space-y-2">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheckIcon className="text-green-500 flex-shrink-0" style={{ width: 14, height: 14 }} />
            <span>Report scams &amp; protect the community</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <UserCircleIcon className="text-blue-500 flex-shrink-0" style={{ width: 14, height: 14 }} />
            <span>Post, connect &amp; interact with members</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheckIcon className="text-amber-500 flex-shrink-0" style={{ width: 14, height: 14 }} />
            <span>Read-only access without an account</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 px-6 pb-6">
          <Link
            href="/login"
            className="w-full py-3 rounded-xl gradient-amber text-primary-foreground font-bold text-sm text-center hover:opacity-90 active:scale-[0.98] transition-all"
            onClick={onClose}
          >
            Sign In
          </Link>
          <Link
            href="/signup"
            className="w-full py-3 rounded-xl border border-border text-foreground font-semibold text-sm text-center hover:bg-muted active:scale-[0.98] transition-all"
            onClick={onClose}
          >
            Create Account
          </Link>
          <button
            onClick={onClose}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors mt-1"
          >
            Continue browsing (read-only)
          </button>
        </div>
      </div>
    </div>
  );
}
